import { ComponentType, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { fontFamilyCorpoSText, getColors } from '../tokens';
import { AcademyIcon, HomeIcon, ViewGridDotsIcon } from '../icons';
import { useSidebar } from './SidebarContext';
import { MBWordmark, ODPLogoDark, ODPLogoLight, ODPLogoMark } from '../assets';
import ArticleIcon from '@mui/icons-material/Article';

/**
 * Singleton history store — patches pushState/replaceState exactly once and
 * fans out to all subscribers. Multiple NavItemLink instances all share a
 * single set of listeners, so cleanup never accidentally unpatches the global.
 */
const _historyListeners = new Set<() => void>();
let _historyPatched = false;

function _ensureHistoryPatched() {
  if (_historyPatched) return;
  _historyPatched = true;

  const notify = () => _historyListeners.forEach(fn => fn());

  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);

  history.pushState = (...args: Parameters<typeof history.pushState>) => {
    origPush(...args);
    notify();
  };
  history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
    origReplace(...args);
    notify();
  };

  window.addEventListener('popstate', notify);
}

function subscribeToHistory(callback: () => void): () => void {
  _ensureHistoryPatched();
  _historyListeners.add(callback);
  return () => {
    _historyListeners.delete(callback);
  };
}

function getPathname(): string {
  return window.location.pathname;
}

function useWindowPathname(): string {
  return useSyncExternalStore(subscribeToHistory, getPathname, getPathname);
}

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

const GRADIENT_ID = 'odp-sidebar-icon-gradient';

interface NavItem {
  label: string;
  icon: ComponentType<{ fontSize?: 'inherit' | 'small' | 'medium' | 'large' }>;
  route: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: HomeIcon, route: '/home' },
  { label: 'Catalog', icon: ViewGridDotsIcon, route: '/catalog' },
  // { label: 'Journeys', icon: JourneyIcon, route: '/journeys' },
  { label: 'Documentation', icon: ArticleIcon, route: '/docs' },
  // { label: 'Shop', icon: CartIcon, route: '/bazaar' },
  { label: 'Academy', icon: AcademyIcon, route: '/academy' },
];

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const borderAlpha = isLight
    ? 'rgba(0, 0, 0, 0.06)'
    : 'rgba(255, 255, 255, 0.06)';
  return {
    sidebar: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      backgroundColor: c.background.sidebar,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1200,
      borderRight: `1px solid ${c.border.default}`,
      fontFamily: fontFamilyCorpoSText,
      overflow: 'hidden',
    },
    logoSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      color: 'inherit',
      position: 'relative' as const,
      overflow: 'hidden',
    },
    logoExpanded: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    logoMark: {
      position: 'absolute' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    nav: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    navLink: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '10px',
      textDecoration: 'none',
      color: c.sidebar.inactiveText,
      fontSize: '13px',
      fontWeight: 400,
      position: 'relative' as const,
      transition: `background-color 0.15s ease, color 0.15s ease`,
      borderBottom: `1px solid ${borderAlpha}`,
      '&:first-of-type': {
        borderTop: `1px solid ${borderAlpha}`,
      },
      '&:hover': {
        backgroundColor: c.sidebar.hover,
        color: c.sidebar.hoverText,
        textDecoration: 'none',
      },
    },
    navLinkActive: {
      color: c.primary.main,
      fontWeight: 600,
      backgroundColor: isLight
        ? 'rgba(0, 120, 214, 0.08)'
        : 'rgba(0, 120, 214, 0.15)',
      boxShadow: `inset 2px 0 0 ${c.primary.main}`,
      borderRadius: '0 6px 6px 0',
      '&:hover': {
        backgroundColor: isLight
          ? 'rgba(0, 120, 214, 0.12)'
          : 'rgba(0, 120, 214, 0.20)',
        color: c.primary.main,
      },
    },
    navIcon: {
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    navIconActive: {
      '& svg path': {
        fill: `url(#${GRADIENT_ID})`,
      },
    },
    navLabel: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: c.text.secondary,
      fontFamily: fontFamilyCorpoSText,
      overflow: 'hidden',
    },
    gradientDefs: {
      position: 'absolute',
      width: 0,
      height: 0,
      overflow: 'hidden',
    },
  };
});

interface NavItemLinkProps {
  item: NavItem;
  collapsed: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  cx: ReturnType<typeof useStyles>['cx'];
}

const NavItemLink = ({ item, collapsed, classes, cx }: NavItemLinkProps) => {
  const currentPath = useWindowPathname();
  const active =
    currentPath === item.route ||
    currentPath.startsWith(`${item.route}/`) ||
    (item.route === '/home' && (currentPath === '/' || currentPath === ''));

  return (
    <Link
      to={item.route}
      className={cx(classes.navLink, active && classes.navLinkActive)}
      style={{
        padding: collapsed ? '11px 0' : '11px 20px',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      <span className={cx(classes.navIcon, active && classes.navIconActive)}>
        <item.icon fontSize="small" />
      </span>
      <span
        className={classes.navLabel}
        style={{ opacity: collapsed ? 0 : 1, maxWidth: collapsed ? 0 : 160 }}
      >
        {item.label}
      </span>
    </Link>
  );
};

export const ODPSidebar = () => {
  const { collapsed } = useSidebar();
  const { classes, cx, theme } = useStyles();
  const isLight = theme.palette.mode === 'light';

  const expandedLogo = isLight ? (
    <ODPLogoLight width={120} height={69} />
  ) : (
    <ODPLogoDark width={120} height={69} />
  );

  return (
    <aside
      data-odp-nav
      className={classes.sidebar}
      style={{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
    >
      <svg className={classes.gradientDefs} aria-hidden="true">
        <defs>
          <linearGradient id={GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0078D6" />
            <stop offset="100%" stopColor="#17E3CC" />
          </linearGradient>
        </defs>
      </svg>

      <Link
        to="/home"
        className={classes.logoSection}
        style={{
          padding: collapsed ? '32px 12px 24px' : '32px 12px 30px',
          minHeight: 80,
        }}
      >
        {collapsed ? <ODPLogoMark width={20} height={33} /> : expandedLogo}
      </Link>

      <nav className={classes.nav}>
        {navItems.map(item => (
          <NavItemLink
            key={item.route}
            item={item}
            collapsed={collapsed}
            classes={classes}
            cx={cx}
          />
        ))}
      </nav>

      {!collapsed && (
        <div className={classes.footer} style={{ padding: '16px 20px 24px' }}>
          <MBWordmark width={100} height={12} />
        </div>
      )}
    </aside>
  );
};
